import React, { useState } from "react";
import { motion } from "framer-motion";
import { formatDateTime } from "../../globalFunctions/DateTimeDisplayCleaner";
import { ShareIcon, CheckIcon, EyeIcon } from "@heroicons/react/24/outline";

export default function NewsArticleContent({ article }) {
  if (!article) return null;

  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `https://ananassolutions.com/actualites/${article.slug}`;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto text-primary px-4">
      {/* IMAGE */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="w-full h-[260px] sm:h-[320px] md:h-[380px] overflow-hidden"
      >
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* META + SHARE + VIEWS */}
      <div className="mt-6 flex flex-wrap justify-between items-center gap-3">
        {/* LEFT SIDE: published + views */}
        <div className="flex items-center gap-4">
          {/* META */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.45 }}
            className="text-sm text-gray-600 border-l-4 border-yellow-400 pl-3 py-1"
          >
            Publié le {formatDateTime(article.publishDate, true)} par{" "}
            <span className="font-semibold">
              {article.companyName || "Compagnie"}
            </span>
          </motion.p>

          {/* VIEWS */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45 }}
            className="flex items-center gap-1 text-gray-600 text-sm"
          >
            <EyeIcon className="w-4 h-4" />
            <span>{article.viewCount || 0}</span>
          </motion.div>
        </div>

        {/* SHARE BUTTON */}
        <button
          onClick={handleShare}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition 
            ${
              copied
                ? "border-green-500 text-green-600 bg-green-50 shadow-sm"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
        >
          {copied ? (
            <CheckIcon className="w-4 h-4" />
          ) : (
            <ShareIcon className="w-4 h-4" />
          )}
          {copied ? "Copié !" : "Partager"}
        </button>
      </div>

      {/* TITLE */}
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.45 }}
        className="mt-6 text-4xl font-bold leading-tight tracking-tight text-[#222]"
      >
        {article.title}
      </motion.h1>

      {/* TEXT BODY */}
      <div className="mt-6 space-y-6 leading-[1.8] text-[1.1rem] text-[#2f2f2f] font-[system-ui]">
        {article.text
          .split("\n")
          .filter((p) => p.trim() !== "")
          .map((para, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "200px" }}
              transition={{ duration: 0.5 }}
              className="whitespace-pre-line"
            >
              {para}
            </motion.p>
          ))}
      </div>
    </div>
  );
}
